/**
 * `/forgot-password` — ask for a reset link.
 *
 * Captcha-protected because this endpoint makes the platform send mail to an
 * address the caller chose, which is a relay worth abusing whether the target
 * has an account or not.
 *
 * The success screen never says whether the address is registered. The API is
 * careful to answer identically either way, and a UI that said "no account
 * found" would hand back the membership oracle the API just refused to be.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import type { MailSentResponse } from "@/api/types";
import { CaptchaField, useCaptcha } from "@/components/Captcha";

export function ForgotPassword() {
  const captcha = useCaptcha("password-reset");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.anonymousPost<MailSentResponse>("/api/v1/auth/password-reset", {
        email,
        ...captcha.fields(),
      });
      setSent(true);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? (cause.fieldMessage ?? cause.message) : "Could not send the email",
      );
    } finally {
      setBusy(false);
      // The challenge is burned on every attempt, success included.
      void captcha.reload();
    }
  }

  if (sent) {
    return (
      <main className="page container" style={{ display: "grid", placeItems: "center" }}>
        <div className="panel panel--narrow" style={{ marginTop: 80 }}>
          <h1 style={{ marginBottom: 6 }}>Check your email</h1>
          <p style={{ color: "var(--paper-300)", fontSize: 14, lineHeight: 1.6 }}>
            If <strong>{email}</strong> has an account, a reset link is on its way. It works once and
            expires in an hour.
          </p>
          <p style={{ color: "var(--paper-400)", fontSize: 13, lineHeight: 1.6 }}>
            Choosing a new password signs out every device currently using the account.
          </p>
          <Link className="btn btn--ghost btn--block" to="/signin" style={{ marginTop: 18 }}>
            Back to sign in
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page container" style={{ display: "grid", placeItems: "center" }}>
      <form className="panel panel--narrow" onSubmit={submit} style={{ marginTop: 80 }}>
        <h1 style={{ marginBottom: 6 }}>Reset your password</h1>
        <p style={{ color: "var(--paper-400)", fontSize: 13, marginTop: 0, marginBottom: 22 }}>
          Tell us the address on the account and we will send a link.
        </p>
        {error && <div className="alert alert--error">{error}</div>}
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <CaptchaField state={captcha} />
        <button className="btn btn--accent btn--block" type="submit" disabled={busy}>
          {busy ? "Sending…" : "Send the reset link"}
        </button>
        <p style={{ marginTop: 18, color: "var(--paper-400)", fontSize: 13 }}>
          Remembered it? <Link to="/signin" style={{ color: "var(--accent)" }}>Sign in</Link>
        </p>
      </form>
    </main>
  );
}
